import { forwardRef, useMemo } from 'react';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useBreakpoint } from '../../layouts/hooks/useBreakpoint.js';
import { useOverlayWithTrigger } from '../../overlays/hooks/useOverlayWithTrigger.js';
import { Overlay } from '../../overlays/overlay/Overlay.js';
import type { basePlacementsType } from '../../overlays/types/placement.js';
import { Content } from './Content.js';
import { InformationOverlayContext } from './contexts/InformationOverlayContext.js';
import { COMPONENT_NAME } from './information-overlay-constants.js';
import { InformationOverlayIcon } from './InformationOverlayIcon.js';
import { Trigger } from './Trigger.js';

//#region Typings
/**
 * Accepted properties for InformationOverlay
 * @public
 */
export interface InformationOverlayBaseProps
  extends
    AriaLabelingProps,
    WithChildren,
    DataTestId,
    MaskingProps,
    StylingProps,
    BehaviorTrackingProps {
  /**
   * The preferred placement for the overlay if there is enough space. If the preferred placement is not possible, the overlay will be positioned according to the available space.
   * @defaultValue 'bottom'
   */
  placement?: basePlacementsType;
  /**
   * Controls the color of the styling and the trigger icon.
   * @defaultValue 'neutral' or a container's color.
   */
  color?: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';
}

/**
 * @public
 */
export interface InformationOverlayUncontrolledProps {
  /**
   * Determines whether the overlay is initially opened in an uncontrolled scenario.
   * @defaultValue false
   */
  defaultOpen?: boolean;
  /**
   * Determines whether the overlay is open in a controlled scenario.
   */
  open?: never;
  /**
   * Callback fired when the open attribute changes in a controlled scenario.
   */
  onOpenChange?: never;
}

/**
 * @public
 */
export interface InformationOverlayControlledProps {
  /**
   * Determines whether the overlay is initially opened in an uncontrolled scenario.
   */
  defaultOpen?: never;
  /**
   * Determines whether the overlay is open in a controlled scenario.
   */
  open: boolean;
  /**
   * Callback fired when the open attribute changes in a controlled scenario.
   */
  onOpenChange: (isOpen: boolean) => void;
}

/**
 * Accepted properties for InformationOverlay
 * @public
 */
export type InformationOverlayProps = InformationOverlayBaseProps &
  (InformationOverlayUncontrolledProps | InformationOverlayControlledProps);

//#endregion Typings

const InformationOverlayRoot = forwardRef<
  HTMLButtonElement,
  InformationOverlayProps
>((props, forwardedRef) => {
  const {
    children,
    defaultOpen,
    open,
    onOpenChange,
    color = 'neutral',
    placement = 'bottom',
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);
  const overlayContentId = useId();

  const [isOpen, setIsOpen] = useControllableState({
    defaultValue: defaultOpen,
    value: open,
    onChange: onOpenChange,
  });

  const matchesMaxWidthWindow = useBreakpoint('(max-width: 480px)');

  const {
    targetRef,
    triggerRef,
    overlayRef,
    overlayState,
    overlayProps,
    overlayTriggerProps,
  } = useOverlayWithTrigger({
    placement: `${placement}-start`,
    placementFallbacks: [
      'top-start',
      'bottom-start',
      'right-start',
      'left-start',
    ],
    offset: 4,
    crossOffset: -16,
    widthStrategy: matchesMaxWidthWindow ? { max: 300 } : { max: 400 },
    open: isOpen,
    onOpenChange: setIsOpen,
    shouldCloseOnBlur: true,
  });
  const overlayStateIsOpen = overlayState.isOpen;

  const rootRef = useMergeRefs([forwardedRef, triggerRef, targetRef]);

  const [slots] = useSlots(children, {
    trigger: Trigger,
    content: Content,
  });

  const { children: triggerChildren, ...triggerProps } =
    slots.trigger?.props ?? {};

  const contextProviderValue = useMemo(() => {
    return { color, overlayTriggerProps, id: overlayContentId, isOpen };
  }, [color, isOpen, overlayContentId, overlayTriggerProps]);

  return (
    <InformationOverlayContext.Provider value={contextProviderValue}>
      <Trigger
        ref={rootRef}
        id={triggerProps.id}
        className={triggerProps.className}
        style={triggerProps.style}
        aria-label={triggerProps['aria-label']}
        aria-describedby={overlayStateIsOpen ? overlayContentId : undefined}
        data-testid={triggerProps['data-testid']}
        data-dtrum-mask={triggerProps['data-dtrum-mask'] ?? dataDtrumMask}
        data-dtrum-allow={triggerProps['data-dtrum-allow'] ?? dataDtrumAllow}
        {...triggerProps}
      >
        {triggerChildren}
      </Trigger>
      <Overlay
        ref={overlayRef}
        isOpen={overlayStateIsOpen}
        overlayProps={overlayProps}
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        <Flex
          id={overlayContentId}
          paddingY={12}
          paddingX={16}
          flexDirection="column"
        >
          {slots.content}
        </Flex>
      </Overlay>
    </InformationOverlayContext.Provider>
  );
});

(
  InformationOverlayRoot as typeof InformationOverlayRoot & {
    displayName: string;
  }
).displayName = COMPONENT_NAME;

/**
 * The `InformationOverlay` provides additional information via an overlay, the position of which can be adjusted.
 * According to the type of information, the `variant` can be set. The trigger consists of an icon and an optional text.
 * By default, the `variant` is `neutral` and the overlay is positioned on the bottom.
 * @public
 */
export const InformationOverlay = Object.assign(InformationOverlayRoot, {
  Content,
  Trigger,
  Icon: InformationOverlayIcon,
});
